import { createAsyncThunk } from '@reduxjs/toolkit';

import { fetchWrapper } from '../../../../helpers';
import { TREATMENT_PLANS_V2_API_BASE } from '../../constants';

import type { IMedication } from '../../../../pages/common';
import type {
  ITreatmentPlanV3MedicationPage,
  GetTreatmentPlanV3MedicationPagePayload,
  CreateTreatmentPlanV3MedicationPayload,
  UpdateTreatmentPlanV3MedicationPayload,
  DeleteTreatmentPlanV3MedicationPayload,
  DeleteAllTreatmentPlanV3MedicationsPayload,
  SetTreatmentPlanV3NoMedicationPayload,
} from './types';

export function createTreatmentPlanV3MedicationThunks() {
  return {
    getTreatmentPlanV3MedicationPage: getTreatmentPlanV3MedicationPage(),
    createTreatmentPlanV3Medication: createTreatmentPlanV3Medication(),
    updateTreatmentPlanV3Medication: updateTreatmentPlanV3Medication(),
    deleteTreatmentPlanV3Medication: deleteTreatmentPlanV3Medication(),
    deleteAllTreatmentPlanV3Medications: deleteAllTreatmentPlanV3Medications(),
    setTreatmentPlanV3NoMedication: setTreatmentPlanV3NoMedication(),
  };
}

function getTreatmentPlanV3MedicationPage() {
  return createAsyncThunk<ITreatmentPlanV3MedicationPage, GetTreatmentPlanV3MedicationPagePayload>(
    'treatmentPlanV3/getTreatmentPlanV3MedicationPage',
    async ({ treatmentPlanId, getAccessTokenSilently }) =>
      fetchWrapper.get({
        url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/medications/`,
        getAccessTokenSilently,
      }),
  );
}

function createTreatmentPlanV3Medication() {
  return createAsyncThunk<IMedication, CreateTreatmentPlanV3MedicationPayload>(
    'treatmentPlanV3/createTreatmentPlanV3Medication',
    async ({ treatmentPlanId, data, getAccessTokenSilently }) =>
      fetchWrapper.post(
        {
          url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/medications/`,
          getAccessTokenSilently,
        },
        data,
      ),
  );
}

function updateTreatmentPlanV3Medication() {
  return createAsyncThunk<IMedication, UpdateTreatmentPlanV3MedicationPayload>(
    'treatmentPlanV3/updateTreatmentPlanV3Medication',
    async ({ treatmentPlanId, medicationId, data, getAccessTokenSilently }) =>
      fetchWrapper.patch(
        {
          url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/medications/${medicationId}/`,
          getAccessTokenSilently,
        },
        data,
      ),
  );
}

function deleteTreatmentPlanV3Medication() {
  return createAsyncThunk<void, DeleteTreatmentPlanV3MedicationPayload>(
    'treatmentPlanV3/deleteTreatmentPlanV3Medication',
    async ({ treatmentPlanId, medicationId, getAccessTokenSilently }) => {
      await fetchWrapper.delete({
        url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/medications/${medicationId}/`,
        getAccessTokenSilently,
      });
    },
  );
}

function deleteAllTreatmentPlanV3Medications() {
  return createAsyncThunk<void, DeleteAllTreatmentPlanV3MedicationsPayload>(
    'treatmentPlanV3/deleteAllTreatmentPlanV3Medications',
    async ({ treatmentPlanId, getAccessTokenSilently }) => {
      await fetchWrapper.delete({
        url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/medications/all/`,
        getAccessTokenSilently,
      });
    },
  );
}

function setTreatmentPlanV3NoMedication() {
  return createAsyncThunk<ITreatmentPlanV3MedicationPage, SetTreatmentPlanV3NoMedicationPayload>(
    'treatmentPlanV3/setTreatmentPlanV3NoMedication',
    async ({ treatmentPlanId, noMedication, getAccessTokenSilently }) =>
      fetchWrapper.patch(
        {
          url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/medications/`,
          getAccessTokenSilently,
        },
        { no_medication: noMedication },
      ),
  );
}
